/**
 * 録音オブジェクトを生成するファクトリ。useRoleplayAudio から使用する。
 * setError/setStatus の代わりに onErrorRef を呼ぶ。
 */
import { isIOS } from '../../utils/deviceUtils';
import { WebRTCAudioRecorderImpl } from '../../utils/webrtcRecorder';
import type { AudioRecording } from './types';
import type { ErrorState } from './types';

export type OnErrorRef = { current: ((err: ErrorState) => void) | undefined };

export function createAudioRecorder(onErrorRef: OnErrorRef): AudioRecording {
  const reportError = (err: ErrorState) => {
    onErrorRef.current?.(err);
  };

  return {
    mediaRecorder: null,
    webrtcRecorder: null,
    audioChunks: [],
    mimeType: '',
    stream: null,
    useWebRTC: isIOS(),

    dataAvailableHandler: function (this: AudioRecording, event: BlobEvent) {
      const timestamp = new Date().toISOString();
      console.log(`[${timestamp}] dataAvailable event:`, {
        hasData: !!event.data,
        size: event.data?.size || 0,
        type: event.data?.type || 'unknown',
        chunksCount: this.audioChunks.length,
        platform: isIOS() ? 'iOS' : 'other',
      });
      if (event.data && event.data.size > 0) {
        this.audioChunks.push(event.data);
        console.log(`Audio chunk ${this.audioChunks.length} added:`, event.data.size, 'bytes');
      } else {
        console.warn('Empty or null audio chunk received:', {
          hasData: !!event.data,
          size: event.data?.size,
          type: event.data?.type,
          totalChunks: this.audioChunks.length,
        });
      }
    },

    errorHandler: function (this: AudioRecording, event: Event) {
      const error = (event as { error?: Error })?.error || new Error('Unknown MediaRecorder error');
      const errorMessage = `録音エラー: ${error.name || 'N/A'} - ${error.message || '詳細不明'}`;
      reportError({ type: 'audio', message: errorMessage });
      this.cleanup();
    },

    initialize: async function (this: AudioRecording, preferredMimeTypes: string[], deviceId?: string) {
      this.cleanup();
      console.log('Initializing AudioRecorder...', {
        useWebRTC: this.useWebRTC,
        platform: isIOS() ? 'iOS' : 'other',
        deviceId: deviceId || 'default',
      });
      try {
        if (this.useWebRTC) {
          console.log('Using WebRTC for iOS audio recording');
          this.webrtcRecorder = new WebRTCAudioRecorderImpl();
          this.webrtcRecorder.onError = (error: Error) => {
            console.error('WebRTC Recorder error:', error);
            reportError({ type: 'audio', message: `WebRTC録音エラー: ${error.message}` });
            this.cleanup();
          };
          this.webrtcRecorder.onDataAvailable = (blob: Blob) => {
            console.log('WebRTC data available:', { size: blob.size, type: blob.type });
            this.audioChunks.push(blob);
          };
          await this.webrtcRecorder.initialize(preferredMimeTypes, deviceId);
          this.mimeType = this.webrtcRecorder.mimeType;
          this.stream = this.webrtcRecorder.stream;
          console.log('WebRTC AudioRecorder initialized successfully');
        } else {
          console.log('Using MediaRecorder for audio recording', { deviceId: deviceId || 'default' });
          const constraints: MediaStreamConstraints = {
            audio: deviceId ? { deviceId: { exact: deviceId } } : true,
            video: false,
          };
          const stream = await navigator.mediaDevices.getUserMedia(constraints);
          this.stream = stream;
          let selectedMimeType: string | undefined;
          const options: MediaRecorderOptions = {};
          for (const mimeType of preferredMimeTypes) {
            if (MediaRecorder.isTypeSupported(mimeType)) {
              selectedMimeType = mimeType;
              options.mimeType = selectedMimeType;
              break;
            }
          }
          if (!selectedMimeType) console.warn('No preferred MIME type supported. Using browser default.');
          const recorder = new MediaRecorder(stream, options);
          this.mimeType = recorder.mimeType;
          recorder.ondataavailable = this.dataAvailableHandler.bind(this);
          recorder.onerror = this.errorHandler.bind(this);
          this.mediaRecorder = recorder;
          console.log('MediaRecorder initialized successfully with device:', deviceId || 'default');
        }
      } catch (error) {
        console.error('Failed to initialize audio recorder:', error);
        this.cleanup();
        const errorMessage = error instanceof Error ? `${error.name}: ${error.message}` : 'Unknown error';
        throw new Error(`マイク初期化失敗: ${errorMessage}`);
      }
    },

    start: function (this: AudioRecording) {
      if (this.useWebRTC) {
        if (!this.webrtcRecorder) {
          console.error('WebRTC Recorder is not initialized');
          return;
        }
        if (this.webrtcRecorder.isRecording) {
          console.warn('WebRTC Recorder is already recording');
          return;
        }
        this.audioChunks = [];
        try {
          console.log('Starting WebRTC recording...');
          this.webrtcRecorder.start();
        } catch (error) {
          console.error('Error starting WebRTC Recorder:', error);
          this.webrtcRecorder.onError?.(error instanceof Error ? error : new Error('WebRTC start failed'));
        }
        return;
      }
      if (!this.mediaRecorder) {
        console.error('MediaRecorder is not initialized');
        return;
      }
      if (this.mediaRecorder.state === 'recording') {
        console.warn('MediaRecorder is already recording');
        return;
      }
      if (this.mediaRecorder.state === 'paused') {
        try {
          this.mediaRecorder.stop();
          setTimeout(() => {
            if (this.mediaRecorder && this.mediaRecorder.state === 'inactive') this.start();
          }, 100);
        } catch (e) {
          console.error('Error stopping paused MediaRecorder:', e);
        }
        return;
      }
      if (this.mediaRecorder.state !== 'inactive') {
        console.warn('MediaRecorder not in inactive state:', this.mediaRecorder.state);
        this.cleanup();
        setTimeout(() => {
          reportError({ type: 'audio', message: '録音機能の再初期化が必要です。少し待ってから再度お試しください。' });
        }, 100);
        return;
      }
      this.audioChunks = [];
      try {
        const timeslice = 500;
        console.log('Starting recording with timeslice:', timeslice, 'Platform:', isIOS() ? 'iOS' : 'other');
        this.mediaRecorder.start(timeslice);
        setTimeout(() => {
          if (this.mediaRecorder && this.mediaRecorder.state !== 'recording') {
            console.error('Failed to start recording. Current state:', this.mediaRecorder.state);
            this.errorHandler(new ErrorEvent('error', { error: new Error('Recording failed to start') }));
          }
        }, 200);
      } catch (error) {
        console.error('Error starting MediaRecorder:', error);
        this.errorHandler(new ErrorEvent('error', { error: error as Error }));
      }
    },

    stop: function (this: AudioRecording) {
      return new Promise<Blob>((resolve, reject) => {
        if (this.useWebRTC) {
          if (!this.webrtcRecorder) {
            resolve(new Blob([], { type: this.mimeType || 'audio/wav' }));
            return;
          }
          if (!this.webrtcRecorder.isRecording) {
            if (this.audioChunks.length > 0) {
              const blobType = this.audioChunks[0]?.type || this.mimeType || 'audio/wav';
              resolve(new Blob(this.audioChunks, { type: blobType }));
            } else {
              resolve(new Blob([], { type: this.mimeType || 'audio/wav' }));
            }
            return;
          }
          try {
            this.webrtcRecorder.stop()
              .then((blob) => resolve(blob))
              .catch((err) => reject(err));
          } catch (error) {
            reject(error instanceof Error ? error : new Error('WebRTC stop failed'));
          }
          return;
        }
        if (!this.mediaRecorder) {
          resolve(new Blob([], { type: this.mimeType || 'audio/webm' }));
          return;
        }
        if (this.mediaRecorder.state === 'inactive') {
          if (this.audioChunks.length > 0) {
            const blobType = this.audioChunks[0]?.type || this.mimeType || 'audio/webm';
            resolve(new Blob(this.audioChunks, { type: blobType }));
          } else {
            resolve(new Blob([], { type: this.mimeType || 'audio/webm' }));
          }
          return;
        }
        const timeout = setTimeout(() => {
          try {
            const blobType =
              (this.audioChunks.length > 0 && this.audioChunks[0]?.type) || this.mimeType || 'audio/webm';
            resolve(new Blob(this.audioChunks, { type: blobType }));
          } catch {
            reject(new Error('Recording stop timeout and blob creation failed'));
          }
        }, 5000);
        const onStopHandler = () => {
          clearTimeout(timeout);
          try {
            if (this.audioChunks.length === 0) {
              reject(new Error('No audio data recorded - 録音時間が短すぎるか、マイクが機能していません。'));
              return;
            }
            const blobType = this.audioChunks[0]?.type || this.mimeType || 'audio/webm';
            const blob = new Blob(this.audioChunks, { type: blobType });
            if (blob.size === 0) reject(new Error('Recorded audio blob is empty'));
            else resolve(blob);
          } catch (error) {
            reject(error);
          } finally {
            if (this.mediaRecorder) {
              this.mediaRecorder.onstop = null;
              this.mediaRecorder.onerror = null;
            }
          }
        };
        this.mediaRecorder.onstop = onStopHandler;
        this.mediaRecorder.onerror = (event) => {
          clearTimeout(timeout);
          this.errorHandler.call(this, event);
          reject((event as { error?: Error }).error || new Error('MediaRecorder error during stop'));
          if (this.mediaRecorder) {
            this.mediaRecorder.onstop = null;
            this.mediaRecorder.onerror = null;
          }
        };
        try {
          if (this.mediaRecorder.state === 'recording' || this.mediaRecorder.state === 'paused') {
            this.mediaRecorder.stop();
          } else {
            onStopHandler();
          }
        } catch (error) {
          clearTimeout(timeout);
          reject(error);
        }
      });
    },

    isRecording: function (this: AudioRecording) {
      if (this.useWebRTC) return this.webrtcRecorder?.isRecording ?? false;
      return this.mediaRecorder?.state === 'recording';
    },

    cleanup: function (this: AudioRecording) {
      console.log('Cleaning up AudioRecorder...', { useWebRTC: this.useWebRTC });
      if (this.webrtcRecorder) {
        try {
          this.webrtcRecorder.cleanup();
        } catch (e) {
          console.error('Error cleaning up WebRTC recorder:', e);
        }
        this.webrtcRecorder = null;
      }
      if (this.mediaRecorder) {
        try {
          if (this.mediaRecorder.state === 'recording' || this.mediaRecorder.state === 'paused') {
            this.mediaRecorder.stop();
          }
        } catch (e) {
          console.error('Error stopping recorder during cleanup:', e);
        }
        try {
          this.mediaRecorder.ondataavailable = null;
          this.mediaRecorder.onerror = null;
          this.mediaRecorder.onstop = null;
        } catch (e) {
          console.warn('Error removing event listeners:', e);
        }
        this.mediaRecorder = null;
      }
      if (this.stream) {
        this.stream.getTracks().forEach((track) => {
          try {
            if (track.readyState !== 'ended') track.stop();
          } catch (e) {
            console.error('Error stopping track:', e);
          }
        });
        this.stream = null;
      }
      this.audioChunks = [];
      this.mimeType = '';
      console.log('AudioRecorder cleanup completed.');
    },
  };
}
